import React, { Fragment, useMemo, useState } from 'react';
import { Alert, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import { getSalesId, getSalesValue, useSalesApiList } from '../hooks/useSalesApiResource';

const normalizeLookupType = (lookupType = {}) => ({
  id: Number(getSalesId(lookupType) || 0),
  typeName: String(getSalesValue(lookupType, 'typeName', 'TypeName', 'name', 'Name') || ''),
  isActive: Boolean(getSalesValue(lookupType, 'isActive', 'IsActive') ?? true),
  isSystem: Boolean(getSalesValue(lookupType, 'isSystem', 'IsSystem') ?? false),
});

const normalizeLookup = (lookup = {}) => ({
  id: Number(getSalesId(lookup) || 0),
  lookupTypeId: Number(getSalesValue(lookup, 'lookupTypeId', 'LookupTypeId') || 0),
  name: String(getSalesValue(lookup, 'name', 'Name', 'description', 'Description') || ''),
  sortOrder: Number(getSalesValue(lookup, 'sortOrder', 'SortOrder', 'displayOrder', 'DisplayOrder') || 0),
  isActive: Boolean(getSalesValue(lookup, 'isActive', 'IsActive') ?? true),
});

const normalizeLookupTypes = (rows) => rows.map(normalizeLookupType);
const normalizeLookups = (rows) => rows.map(normalizeLookup);

const LookupList = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedIds, setExpandedIds] = useState(new Set());

  const { items: lookupTypes, loading: typesLoading, error: typesError } = useSalesApiList((endpoints) => endpoints.lookupTypes.base, {
    normalize: normalizeLookupTypes,
    errorMessage: 'Unable to load lookup types.',
  });
  const { items: lookups, loading: lookupsLoading, error: lookupsError } = useSalesApiList((endpoints) => endpoints.lookups.base, {
    normalize: normalizeLookups,
    errorMessage: 'Unable to load lookup values.',
  });

  const lookupsByTypeId = useMemo(() => {
    const grouped = new Map();
    lookups.forEach((lookup) => {
      const current = grouped.get(lookup.lookupTypeId) || [];
      current.push(lookup);
      grouped.set(lookup.lookupTypeId, current);
    });
    grouped.forEach((values) => values.sort((left, right) => left.sortOrder - right.sortOrder || left.name.localeCompare(right.name)));
    return grouped;
  }, [lookups]);

  const filteredTypes = useMemo(() => {
    const needle = searchTerm.trim().toLowerCase();
    if (!needle) return lookupTypes;
    return lookupTypes.filter((lookup) => lookup.typeName.toLowerCase().includes(needle));
  }, [lookupTypes, searchTerm]);

  const toggleExpand = (id) => {
    setExpandedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const loading = typesLoading || lookupsLoading;

  return (
    <section className="sales-portal-page" aria-labelledby="sales-lookups-title">
      <Row className="mb-3">
        <Col><h4 id="sales-lookups-title">Lookup List</h4></Col>
      </Row>
      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <div className="position-relative search-input">
              <i className="bi bi-search position-absolute" style={{ left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#626262' }}></i>
              <Form.Control type="text" placeholder="Search" value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} style={{ paddingLeft: '40px' }} />
            </div>
          </Form.Group>
        </Col>
      </Row>

      {(typesError || lookupsError) && <Alert variant="danger">{typesError || lookupsError}</Alert>}

      <div className="card">
        <div className="card-body p-0">
          <div className="table-responsive">
            <Table hover className="mb-0">
              <thead>
                <tr className="table-border">
                  <th style={{ width: '3em' }}></th>
                  <th style={{ width: '80%' }}>Name</th>
                  <th style={{ width: '10%' }}>Active</th>
                  <th style={{ width: '10%' }}>System</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={4}><div className="sales-no-data"><Spinner animation="border" size="sm" /> Loading lookups...</div></td></tr>
                ) : filteredTypes.length === 0 ? (
                  <tr><td colSpan={4}><div className="sales-no-data">No Data Available</div></td></tr>
                ) : (
                  filteredTypes.map((lookup) => {
                    const isExpanded = expandedIds.has(lookup.id);
                    const details = lookupsByTypeId.get(lookup.id) || [];
                    return (
                      <Fragment key={lookup.id}>
                        <tr className={`table-border${isExpanded ? ' vr-expanded-row' : ''}`}>
                          <td>
                            <button type="button" className="edit-icon" onClick={() => toggleExpand(lookup.id)} aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${lookup.typeName}`}>
                              <i className={`bi bi-chevron-${isExpanded ? 'down' : 'right'}`}></i>
                            </button>
                          </td>
                          <td className="text-left"><span className="text-wrap" title={lookup.typeName}>{lookup.typeName}</span></td>
                          <td className="text-center"><Form.Check type="checkbox" checked={lookup.isActive} disabled readOnly /></td>
                          <td className="text-center">{lookup.isSystem && <i className="bi bi-check-circle-fill text-success" title="System category"></i>}</td>
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td colSpan={4} style={{ padding: '0 0 0 30px', background: '#f8f9fa' }}>
                              <Table size="sm" className="mb-0">
                                <thead>
                                  <tr className="table-border">
                                    <th>Value Name</th>
                                    <th>Sort Order</th>
                                    <th>Active</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {details.length === 0 ? (
                                    <tr><td colSpan={3}><div className="sales-no-data">No lookup values found.</div></td></tr>
                                  ) : details.map((detail) => (
                                    <tr key={detail.id} className="table-border">
                                      <td>{detail.name}</td>
                                      <td>{detail.sortOrder}</td>
                                      <td><Form.Check type="checkbox" checked={detail.isActive} disabled readOnly /></td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LookupList;
